import { CSS_ROMANEIO } from '@/componentes/romaneio/RomaneioPapel';
import { api } from '@/lib/api';

/**
 * Imprime um "papel" A4 num documento próprio, isolado da aplicação.
 *
 * POR QUE UM IFRAME, E NÃO A PRÓPRIA PÁGINA
 * -----------------------------------------
 * `@page` é GLOBAL por documento: a aplicação já declara
 * `@page { size: A4 landscape }` para o roteiro e o kardex. Imprimir a partir
 * da página daria todo papel deitado, e não há como sobrescrever isso por
 * elemento. Um iframe tem documento próprio e, portanto, `@page` próprio.
 *
 * E o documento é AUTOSSUFICIENTE: nenhuma folha de estilo do aplicativo é
 * clonada. Todo o visual vem do `css` que o chamador passa — que é a MESMA
 * constante usada pela prévia em tela. Assim o que se vê e o que sai da
 * impressora não podem divergir.
 *
 * O ERRO QUE ESTA FUNÇÃO EXISTE PARA NÃO REPETIR
 * ----------------------------------------------
 * A primeira versão daqui era específica do romaneio e embutia `CSS_ROMANEIO`
 * fixo. Quando a conferência cega passou a usá-la, o papel saiu da impressora
 * como texto corrido — o CSS dela nunca entrava no iframe, e ninguém percebeu
 * porque a PRÉVIA EM TELA, essa sim, tinha o estilo certo. Por isso o CSS
 * agora é PARÂMETRO OBRIGATÓRIO: não existe caminho em que se esqueça dele.
 *
 * Para gerar PDF, o usuário escolhe "Salvar como PDF" no próprio diálogo do
 * navegador — sai em A4 correto porque o `@page` daqui é o único do documento.
 */
export interface OpcoesImpressao {
  /** O elemento cujo `innerHTML` vira o corpo do documento impresso. */
  conteudo: HTMLElement;
  /** Título do documento — vira o nome sugerido do arquivo em "Salvar como PDF". */
  titulo: string;
  /** Folha de estilo COMPLETA do papel, incluindo o `@page`. */
  css: string;
  /**
   * Seletor do elemento raiz do papel dentro do iframe. Serve só para esperar
   * o layout ficar pronto antes de chamar `print()`; ver `tentar` abaixo.
   */
  seletor: string;
}

/** O documento autossuficiente do papel: o `css` dele e mais nada do aplicativo. */
export function documentoPapel(titulo: string, css: string, html: string): string {
  return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<title>${escaparHtml(titulo)}</title>
<style>
  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    color: #000;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  /* Vale para todo papel: cabeçalho de tabela repete e linha não parte ao meio. */
  thead { display: table-header-group; }
  tr { break-inside: avoid; page-break-inside: avoid; }

${css}
</style>
</head>
<body>${html}</body>
</html>`;
}

function escaparHtml(texto: string): string {
  return texto.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function imprimirPapel({ conteudo, titulo, css, seletor }: OpcoesImpressao): void {
  const html = conteudo.innerHTML.trim();
  if (!html) return;

  document.getElementById('quadro-impressao')?.remove();

  const quadro = document.createElement('iframe');
  quadro.id = 'quadro-impressao';
  /*
   * Fora da tela, mas VISÍVEL e com tamanho: `display:none` e companhia fazem
   * o Chrome pular o layout do iframe, e sem layout ele imprime branco.
   */
  quadro.style.cssText = 'position:fixed;left:-10000px;top:0;width:210mm;height:297mm;border:0;';
  document.body.appendChild(quadro);

  const doc = quadro.contentDocument;
  if (!doc) {
    quadro.remove();
    return;
  }

  doc.open();
  doc.write(documentoPapel(titulo, css, html));
  doc.close();

  let jaImprimiu = false;
  const imprimirAgora = (): void => {
    if (jaImprimiu) return;
    jaImprimiu = true;
    quadro.contentWindow?.focus();
    quadro.contentWindow?.print();
    // Não remove depois de imprimir: ver imprimirEtiquetas — pela caixa de
    // diálogo do sistema, remover em 2 s fazia sair papel em branco.
  };

  /*
   * Só imprime quando houver LAYOUT MEDIDO, e não quando o documento diz estar
   * pronto: num documento recém-escrito o `print()` sai antes de o Chrome medir
   * qualquer coisa, e o papel vem branco.
   */
  const inicio = Date.now();
  const tentar = (): void => {
    const papel = doc.querySelector(seletor) as HTMLElement | null;
    if (papel && papel.getBoundingClientRect().height > 10) {
      quadro.contentWindow?.requestAnimationFrame(imprimirAgora);
      return;
    }
    if (Date.now() - inicio > 3000) {
      imprimirAgora();
      return;
    }
    window.setTimeout(tentar, 50);
  };

  tentar();
}

/** O Romaneio de Expedição — `@page` de 4 mm, como o Procfit usa. */
export function imprimirRomaneio(conteudo: HTMLElement, romaneio: number): void {
  imprimirPapel({
    conteudo,
    titulo: `Romaneio ${romaneio}`,
    seletor: '.romaneio-papel',
    css: `
  @page {
    size: A4;
    margin: 4mm;
    @bottom-right { content: "Pag. " counter(page) " / " counter(pages); font-size: 6.5pt; color: #666; }
    @bottom-left  { content: "Romaneio de Expedicao"; font-size: 6.5pt; color: #666; }
  }
${CSS_ROMANEIO}`,
  });
}

/**
 * Abre o Romaneio de Expedição no EDGE, com a prévia e a escolha de impressora.
 *
 * O Chrome do checkout roda com `--kiosk-printing` (a etiqueta do crachá sai
 * sem diálogo) e por isso imprime tudo na impressora padrão, a Zebra, sem
 * prévia e sem perguntar — o A4 saía nela. O Edge não tem esse modo: abre a
 * página `/romaneio-a4`, que chama o diálogo de impressão. O Edge não tem a
 * sessão do WMS, então leva um token curto, preso ao romaneio (`link-a4` na
 * API). O `preparar-checkout.ps1` libera o Chrome a abrir o Edge sem perguntar.
 *
 * `microsoft-edge:` é registrado pelo Windows: funciona com o Edge onde quer
 * que ele esteja instalado. Lança `ErroHttp` se a API recusar o link.
 */
export async function abrirRomaneioNoEdge(romaneio: number): Promise<void> {
  const { token } = await api.post<{ token: string }>(`/api/romaneios/${romaneio}/link-a4`);
  const url = `${window.location.origin}/romaneio-a4/${romaneio}?t=${encodeURIComponent(token)}`;
  window.location.href = `microsoft-edge:${url}`;
}

/**
 * Abre no EDGE um papel que a tela já montou — a mesma saída do romaneio, para
 * todo A4: conferência cega, kardex, roteiro, folha de inventário.
 *
 * O Chrome do checkout roda com `--kiosk-printing` e manda tudo para a Zebra,
 * sem prévia. O Edge abre a página `/papel-a4/<token>`, com a prévia e o
 * diálogo de impressão. Diferente do romaneio, aqui o Edge não relê dados: a
 * tela grava o HTML que já desenhou (`POST /api/papel-a4`) e o Edge o mostra
 * igual. Um mecanismo só para qualquer papel.
 *
 * Dois modos:
 *  - `papel`: documento autossuficiente com o `css` do próprio papel (o mesmo
 *    de `imprimirPapel`). Leva o `innerHTML` do elemento.
 *  - `pagina`: a `.folha` impressa pelo `@media print` do aplicativo (kardex,
 *    roteiro). Leva o elemento inteiro, sem o que é `.nao-imprimir`; o Edge
 *    carrega as folhas de estilo do próprio WMS.
 *
 * Lança `ErroHttp` se a API recusar.
 */
export async function abrirPapelNoEdge(
  opcoes:
    | { modo: 'papel'; conteudo: HTMLElement; titulo: string; css: string }
    | { modo: 'pagina'; conteudo: HTMLElement; titulo: string },
): Promise<void> {
  let html: string;
  if (opcoes.modo === 'papel') {
    html = opcoes.conteudo.innerHTML.trim();
  } else {
    const copia = opcoes.conteudo.cloneNode(true) as HTMLElement;
    copia.querySelectorAll('.nao-imprimir').forEach((e) => e.remove());
    html = copia.outerHTML;
  }
  if (!html) return;
  const { token } = await api.post<{ token: string }>('/api/papel-a4', {
    titulo: opcoes.titulo,
    modo: opcoes.modo,
    css: opcoes.modo === 'papel' ? opcoes.css : null,
    html,
  });
  window.location.href = `microsoft-edge:${window.location.origin}/papel-a4/${encodeURIComponent(token)}`;
}

/**
 * Abre no EDGE um PDF A4 da API — o DANFE —, com a prévia e a escolha de
 * impressora. Mesmo motivo do romaneio: no checkout o Chrome imprimiria na
 * Zebra. `href` é a rota do PDF (`/api/pedidos/notas/:id/pdf`); a API emite um
 * token curto em `<href>/link-edge` e serve o PDF sem sessão em `<href>/edge`.
 *
 * Lança `ErroHttp` se a API recusar o link.
 */
export async function abrirPdfNoEdge(href: string): Promise<void> {
  const { token } = await api.post<{ token: string }>(`${href}/link-edge`);
  const pdf = `${href}/edge?t=${encodeURIComponent(token)}`;
  window.location.href = `microsoft-edge:${window.location.origin}/pdf-a4?u=${encodeURIComponent(pdf)}`;
}
